import { addListItemOptionals } from "./types";

class TocHtmlBuilder {
  tocHtmlStructure: string[];
  currentNestingLevel: number;
  constructor() {
    this.tocHtmlStructure = [];
    this.currentNestingLevel = 0;
  }

  getHrefElement(
    elementId: string,
    elementTitle: string,
    optionals?: addListItemOptionals,
  ): string {
    // TODO - this should be changed when the full toc map is created in line with offline toc map
    // Only elementId of the clicked item should be part of the route param
    const routeParameters = optionals?.subTargetId
      ? `'${elementId}',{'screenId':'${optionals.subTargetId}'}`
      : `'${elementId}'`;

    return `<button onclick="route(${routeParameters});">${elementTitle}</button>`;
  }

  startList(id: string): void {
    if (this.currentNestingLevel > 0) {
      this.tocHtmlStructure.push("<ul>");
    } else {
      this.tocHtmlStructure.push(`<ul id=${id}>`);
    }
    this.currentNestingLevel++;
  }

  endList(): void {
    if (this.currentNestingLevel > 0) {
      this.tocHtmlStructure.push("</ul>");
      this.currentNestingLevel--;
    }
  }

  /**
   * Adds a list item for a standard lesson to the Table of Contents (TOC).
   *
   * This function handles the addition of standard lessons to the TOC.
   * It uses the first screen ID of the standard lesson to create the href link
   * and uses the element ID for the list item's ID attribute.
   *
   * @param {string} elementId - The ID of the element, used for the list item's ID attribute.
   * @param {string} elementTitle - The title of the element, displayed as the list item's text.
   * @param {string} standardLessonfirstScreenId - The first screen ID of the standard lesson, used for the href link.
   */
  addStandardLessonListItem(
    elementId: string,
    elementTitle: string,
    standardLessonfirstScreenId: string,
  ): void {
    const hrefElement = this.getHrefElement(standardLessonfirstScreenId, elementTitle);
    this.tocHtmlStructure.push(`<li id=${elementId}>${hrefElement}`);
  }

  /**
   * Adds a list item for a stitched lesson to the Table of Contents (TOC).
   *
   * This function is responsible for adding stitched lessons to the TOC.
   * It uses the stitched lesson's screen ID for both the href link and the list item's ID attribute.
   *
   * @param {string} elementId - The ID of the element, used for the href link.
   * @param {string} elementTitle - The title of the element, displayed as the list item's text.
   * @param {string} stitchedLessonScreenId - The screen ID of the stitched lesson, used for the list item's ID attribute.
   */
  addStitchedLessonListItem(
    elementId: string,
    elementTitle: string,
    stitchedLessonScreenId: string,
  ): void {
    const hrefElement = this.getHrefElement(elementId, elementTitle, {
      subTargetId: stitchedLessonScreenId,
    });
    this.tocHtmlStructure.push(`<li id=${stitchedLessonScreenId}>${hrefElement}`);
  }

  /**
   * Adds a default list item to the Table of Contents (TOC).
   *
   * This function is used for adding a list item when it's neither a standard nor a stitched lesson.
   * It uses the provided element ID for both the href link and the list item's ID attribute.
   *
   * @param {string} elementId - The ID of the element, used for both the href link and the list item's ID attribute.
   * @param {string} elementTitle - The title of the element, displayed as the list item's text.
   */
  addDefaultListItem(elementId: string, elementTitle: string): void {
    const hrefElement = this.getHrefElement(elementId, elementTitle);
    this.tocHtmlStructure.push(`<li id=${elementId}>${hrefElement}`);
  }

  closeListItem(): void {
    this.tocHtmlStructure.push("</li>");
  }

  getStructure(): string {
    return this.tocHtmlStructure.join("");
  }
}

export default TocHtmlBuilder;
